import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Icons } from "../lib/icons";
import { Btn, Pill } from "../lib/primitives";
import { useQuickstart, useCreateApiKey } from "../lib/hooks";

/**
 * "Get started" checklist, shown on Overview until every row is done and on
 * its own at /quickstart (where the onboarding wizard lands). Every row is
 * detected from server state via `/api/quickstart`.
 *
 * The "Generate API key" row also includes an inline mint button so an
 * operator who skipped the wizard's key-mint step doesn't have to leave
 * the screen to remediate.
 */
type RealRowId =
  | "org_created"
  | "api_key_generated"
  | "first_auth"
  | "team_invited"
  | "branding";

type Row = {
  kind: "real";
  id: RealRowId;
  title: string;
  desc: string;
  done: boolean;
  cta: { href: string; label: string };
  /** Optional inline mint button — only for the api_key row when no key exists. */
  inlineGenerate?: boolean;
};

function timeAgo(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86_400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86_400 * 7) return `${Math.floor(diff / 86_400)}d ago`;
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

export function Quickstart() {
  return (
    <>
      <div className="ad-page-head">
        <div className="ad-page-head-l">
          <div className="ad-eyebrow">Overview</div>
          <h1 className="ad-h1">Get started</h1>
          <div className="ad-sub">
            A few steps to take your app from testing to live. Each one opens the page that
            finishes it.
          </div>
        </div>
      </div>
      <GetStarted />
    </>
  );
}

/** The checklist card. With `hideWhenDone` it disappears once complete. */
export function GetStarted({ hideWhenDone = false }: { hideWhenDone?: boolean }) {
  const navigate = useNavigate();
  const { data, isLoading } = useQuickstart();
  const createKey = useCreateApiKey();
  const [revealed, setRevealed] = useState<string | null>(null);

  // While loading, render a thin skeleton — we keep the page chrome so the
  // operator doesn't see a layout shift when the data lands.
  const providerName = data?.providerName ?? (isLoading ? "…" : "Your org");
  const keyCount = data?.apiKeys.activeCount ?? 0;
  const keyPrefix = data?.apiKeys.latestKeyPrefix;
  const firstAuth = data?.firstAuth;
  const team = data?.team;

  const rows: Row[] = [
    {
      kind: "real",
      id: "org_created",
      title: "Create your organization",
      desc: providerName,
      done: true, // if you're seeing this screen, you have a team_member row
      cta: { href: "/settings", label: "Edit" },
    },
    {
      kind: "real",
      id: "api_key_generated",
      title: "Create an API key",
      desc:
        keyCount > 0
          ? `${keyPrefix}••• · ${keyCount} active key${keyCount === 1 ? "" : "s"}`
          : "Your server uses it to start a sign-in.",
      done: keyCount > 0,
      cta: { href: "/keys", label: "API keys" },
      inlineGenerate: keyCount === 0,
    },
    {
      kind: "real",
      id: "first_auth",
      title: "Your first sign-in",
      desc: firstAuth?.hasAuth
        ? `${firstAuth.latestExternalUserId ?? "Someone"} · ${
            firstAuth.latestAuthAt ? timeAgo(firstAuth.latestAuthAt) : "recent"
          }`
        : "We tick this off when the first person signs in.",
      done: Boolean(firstAuth?.hasAuth),
      cta: { href: "/logs", label: "Activity" },
    },
    {
      kind: "real",
      id: "team_invited",
      title: "Invite your team",
      desc:
        team && (team.activeMembers > 1 || team.pendingInvitations > 0)
          ? `${team.activeMembers} member${team.activeMembers === 1 ? "" : "s"}${
              team.pendingInvitations > 0
                ? `, ${team.pendingInvitations} pending`
                : ""
            }`
          : "Add the people who run this app with you.",
      done: Boolean(team && (team.activeMembers > 1 || team.pendingInvitations > 0)),
      cta: { href: "/team", label: "Team" },
    },
    {
      kind: "real",
      id: "branding",
      title: "Make the sign-in page yours",
      desc: data?.brandingConfigured
        ? "Name, logo and color set."
        : "Add your app's name, logo and color.",
      done: Boolean(data?.brandingConfigured),
      cta: { href: "/sign-in-page", label: "Sign-in page" },
    },
  ];

  const done = rows.filter((r) => r.done).length;
  const pct = Math.round((done / rows.length) * 100);

  async function onGenerateKey() {
    try {
      const res = await createKey.mutateAsync("default");
      setRevealed(res.apiKey);
    } catch {
      /* mutation state surfaces the error */
    }
  }

  if (hideWhenDone && (isLoading || done === rows.length) && !revealed) return null;

  return (
    <>
      <div className="ad-card">
        <div className="ad-card-head">
          <div>
            <div className="ad-card-title">Get started</div>
            <div style={{ fontSize: 14, color: "var(--ad-fg-3)", marginTop: 4 }}>
              {done} of {rows.length} done
            </div>
          </div>
          <div style={{ width: 160 }}>
            <div className="ad-progress">
              <div className="ad-progress-bar" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>

        <div className="ad-stack-md">
          {rows.map((row, i) => {
            return (
              <div
                key={row.id}
                className={`ad-onb-item ${row.done ? "is-done" : ""}`}
              >
                <div className="ad-onb-item-num">
                  {row.done ? <Icons.Check size={14} /> : <span>{i + 1}</span>}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{ fontSize: 15, fontWeight: 600, color: "var(--ad-fg-1)" }}
                  >
                    {row.title}
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      color: "var(--ad-fg-3)",
                    }}
                  >
                    {row.desc}
                  </div>
                </div>
                {row.done ? (
                  <Pill kind="success" dot>
                    Done
                  </Pill>
                ) : row.inlineGenerate ? (
                  <Btn
                    kind="primary"
                    size="sm"
                    leading={<Icons.Key size={12} />}
                    onClick={onGenerateKey}
                    disabled={createKey.isPending}
                  >
                    {createKey.isPending ? "Creating…" : "Create key"}
                  </Btn>
                ) : (
                  <Btn
                    kind="ghost"
                    size="sm"
                    onClick={() => navigate(row.cta.href)}
                  >
                    {row.cta.label}
                  </Btn>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {revealed && (
        <div
          className="ad-card"
          style={{
            marginTop: 16,
            background: "var(--ad-orange-soft)",
            borderColor: "transparent",
          }}
        >
          <div className="ad-card-title" style={{ marginBottom: 10 }}>
            Copy your new key now
          </div>
          <div
            style={{
              padding: 12,
              border: "1px solid var(--ad-border-2)",
              borderRadius: 12,
              fontFamily: "var(--rb-font-mono)",
              fontSize: 13,
              wordBreak: "break-all",
              background: "var(--ad-surface-1)",
              color: "var(--ad-fg-1)",
            }}
          >
            {revealed}
          </div>
          <div style={{ fontSize: 14, color: "var(--ad-fg-2)", marginTop: 10 }}>
            It's only shown once. We keep a scrambled copy that can't be turned
            back into the key.
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 12,
            }}
          >
            <Btn kind="primary" size="sm" onClick={() => setRevealed(null)}>
              I've saved it
            </Btn>
          </div>
        </div>
      )}
    </>
  );
}
